import { useCallback, useEffect, useState } from 'react';
import { UpdateCommand } from '../../../commands/UpdateCommand';
import { CommandManager } from '../../../commands/CommandManager';
import CanvasSession from '../../../services/CanvasSession';
import {
  AppState,
  ClientCoordinates,
  DimensionDragInfo,
  SelectionMode,
  SelectionModes,
} from '../../../types';
import {
  calculateDimensionUpdate,
  formatDimensionId,
  getStartOffset,
  parseDimensionId,
} from './dimensionDrag/dimensionDragLogic';

export interface DimensionDraggingDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  setDraggingDimensionId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useDimensionDragging = (hooks: DimensionDraggingDeps) => {
  const {
    appState,
    session,
    commandManager,
    setSelectionMode,
    selectionMode,
    setDraggingDimensionId,
  } = hooks;

  const [dragInfo, setDragInfo] = useState<DimensionDragInfo | null>(null);

  const onStartDimensionDrag = useCallback(
    (clientCoordinates: ClientCoordinates, id: string) => {
      const parsed = parseDimensionId(id);
      if (!parsed) return;

      const element = appState.elements[parsed.elementId];
      if (!element) return;

      const startOffset = getStartOffset(element, parsed);

      setDragInfo({
        ...parsed,
        startOffset,
        startMouseX: clientCoordinates.x,
        startMouseY: clientCoordinates.y,
      });

      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.DimensionDragging,
      });
    },
    [appState.elements, setSelectionMode, selectionMode]
  );

  const onDimensionDragging = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral = true) => {
      if (!dragInfo) return;

      const element = appState.elements[dragInfo.elementId];
      if (!element) return;

      const update = calculateDimensionUpdate(
        element,
        dragInfo,
        clientCoordinates.x,
        clientCoordinates.y
      );

      if (!update) return;

      const command = new UpdateCommand(
        [update.updatedElement],
        [update.originalElement],
        session.currentUserId,
        isEphemeral
      );

      commandManager.executeCommand(command);
    },
    [dragInfo, appState.elements, session.currentUserId, commandManager]
  );

  const onStopDimensionDrag = useCallback(() => {
    if (dragInfo) {
      setDragInfo(null);
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.None,
      });
    }
  }, [dragInfo, setSelectionMode, selectionMode]);

  const draggingDimensionId = dragInfo ? formatDimensionId(dragInfo) : null;

  useEffect(() => {
    setDraggingDimensionId(draggingDimensionId);
  }, [draggingDimensionId, setDraggingDimensionId]);

  return {
    onStartDimensionDrag,
    onDimensionDragging,
    onStopDimensionDrag,
    dragInfo,
    draggingDimensionId,
  };
};
